import {Component, DestroyRef, DoCheck, OnInit, ViewChild} from '@angular/core';
import {PLAN_LODGEMENT_STAGE, PLAN_VALIDATION_STAGE, TABS} from '../common/constants';
import {DependenciesComponent} from './dependencies/dependencies.component';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {PlanModelService} from '../common/service/plan-model.service';
import {DependantPlan} from '../common/models/dependant-plan.model';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {PermissionsService} from '../common/service/permissions.service';

import {FormsModule, NgForm} from '@angular/forms';
import {first, Observable, Subscription} from 'rxjs';
import {RuleResultsResponse} from '../plan-detail/plan-detail.model';
import {switchMap} from 'rxjs/operators';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {NgEventBus} from 'ng-event-bus';
import {AppConfigService} from '../common/service/app-config.service';
import {HttpClient} from '@angular/common/http';
import {DependantPlanResponse} from '../common/models/dependant-plan-response.model';
import {StrataPlanAction} from '../common/models/strata-plan-action.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AlertModalService} from '../common/component/modal';

@Component({
    selector: 'nlr-analysis',
    imports: [DependenciesComponent, FormsModule],
    templateUrl: './analysis.component.html',
    styleUrl: './analysis.component.scss'
})
export class AnalysisComponent implements OnInit, DoCheck {
    @ViewChild('analysisForm')
    analysisForm: NgForm;

    protected readonly title = TABS.ANALYSIS;

    protected readonly stage = PLAN_VALIDATION_STAGE.LODGEMENT_ANALYSIS;

    analysisPermissions = ['EDIT_ANALYSIS', 'VIEW_ANALYSIS'];

    validatePermissions = ['EDIT_PLAN', 'VIEW_PLAN'];

    formerTenureDependenciesFound = false;

    spatialDependenciesFound = false;

    strataPlanDependenciesFound = false;

    descendantDependenciesFound = false;

    private saveAnalysisSubscription: Subscription;

    private validateAnalysisSubscription: Subscription;

    private oldFormerTenures = [];

    private loadTimeout: number;

    constructor(
        private appConfigService: AppConfigService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private http: HttpClient,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private permissionsService: PermissionsService,
        private notificationAlertsService: NotificationAlertsService,
        private nlrPlanDetailService: NlrPlanDetailService,
        private alertModalService: AlertModalService
    ) {}

    ngOnInit() {
        this.saveAnalysisSubscription = this.eventBus
            .on('SAVE_ANALYSIS')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.save());
        this.validateAnalysisSubscription = this.eventBus
            .on<boolean>('VALIDATE_ANALYSIS')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => this.validate(meta.data));
    }

    ngDoCheck() {
        const {formerTenures = [], planType, planNumber, planPurpose} = this.getPlanModel();
        if (formerTenures.length !== this.oldFormerTenures.length || formerTenures.some(formTenure => !this.oldFormerTenures.includes(formTenure))) {
            this.oldFormerTenures.splice(0, this.oldFormerTenures.length, ...formerTenures);

            if (planType && planNumber && planPurpose) {
                if (this.loadTimeout) {
                    window.clearTimeout(this.loadTimeout);
                }
                this.loadTimeout = window.setTimeout(() => this.load(), 1000);
            }
        }
    }

    // loads page - split return set into 3 categories
    private load() {
        const planModel = this.planModelService.getModel();

        if (planModel.planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED) {
            this.loadDependantPlans();
        } else {
            const isNewLodgementType = planModel.lodgementType.value.toUpperCase() === 'NEW';
            const dependentPlansRequest = {
                subjectPlan: planModel.planType.smpId + planModel.planNumber,
                stage: isNewLodgementType ? null : planModel.planStage,
                formerTenures: JSON.parse(JSON.stringify(planModel.formerTenures)),
                calculateDescendantDependencies: !isNewLodgementType,
                previousSpatialDependencies: JSON.parse(JSON.stringify(planModel.dependantPlans.filter(dp => dp.dependencyType === 'SPATIAL')))
            };

            const getDependantPlansSubscription = this.http
                .post(`${this.appConfigService.settings.apiGatewayUrl}/dependantPlan/dependantPlans`, dependentPlansRequest)
                .pipe()
                .pipe(first())
                .subscribe({
                    next: (response: DependantPlanResponse) => {
                        console.debug(`Success retrieving dependant plans. Found records: ${response.dependantPlans.length}`);

                        // determine what to keep and/or add
                        this.mergeDependantPlans(planModel.dependantPlans, response.dependantPlans);
                        this.mergeStrataPlanActions(planModel.strataPlanActions, response.strataPlanActions);

                        this.loadDependantPlans();

                        // set the spatial dependency calc time (if returned)
                        if (response.spatialDependencyCalcDateTime) {
                            planModel.spatialDependencyCalcDateTime = response.spatialDependencyCalcDateTime;
                        }

                        // show an error, but otherwise continue as per normal
                        if (response.errorMessage) {
                            this.alertModalService.createAlertMessage(response.errorMessage, 'Cancel', 'Warning', 'warning');
                        }
                    },
                    error: error => {
                        const errorMessage = 'Error retrieving Dependant Plans and Strata Dependencies.';
                        console.error(`${errorMessage} Error content:\n${error}`);
                        this.notificationAlertsService.addMessage({text: errorMessage});
                    }
                });
            this.eventBus.cast('nlr:busy', getDependantPlansSubscription);
        }
    }

    loadDependantPlans() {
        const {strataPlanActions = [], dependantPlans = []} = this.getPlanModel();
        this.strataPlanDependenciesFound = strataPlanActions.length > 0;

        const dependencyTypes = dependantPlans.map(dependantPlan => dependantPlan.dependencyType);

        this.descendantDependenciesFound = dependencyTypes.includes('DESCENDANT');
        this.formerTenureDependenciesFound = dependencyTypes.includes('FORMER_TENURE');
        this.spatialDependenciesFound = dependencyTypes.includes('SPATIAL');

        console.debug(`strataPlanActions count = ${strataPlanActions.length}`);
        console.debug(`dependantPlans count = ${dependantPlans.length}`);
    }

    getPlanModel() {
        return this.planModelService.getModel();
    }

    hasMinimumPlanDetails() {
        return this.planLodgementService.hasMinimumPlanDetails();
    }

    saveDisabled() {
        return (
            !this.hasMinimumPlanDetails() || this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.analysisPermissions)
        );
    }

    save() {
        console.debug(`${this.title} component - save`);

        if (this.saveDisabled()) {
            return;
        }

        const subscription = this.planLodgementService.save().subscribe({complete: () => subscription.unsubscribe()});
        this.eventBus.cast('nlr:busy', subscription);
    }

    cancelDisabled() {
        return this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.analysisPermissions);
    }

    cancel() {
        console.debug(`${this.title} component - cancel`);

        this.planLodgementService.cancel();
    }

    validateDisabled() {
        return (
            !this.hasMinimumPlanDetails() || this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.validatePermissions)
        );
    }

    validate(skipSave = false) {
        console.debug(`${this.title} component - validate`);

        if (this.validateDisabled()) {
            return;
        }

        this.analysisForm.onSubmit(undefined);

        if (this.analysisForm.invalid) {
            this.notificationAlertsService.addMessage({
                subType: 'AnalysisForm',
                text: 'The Analysis page is incomplete or data entered is invalid!'
            });
        }

        let runValidations: Observable<RuleResultsResponse>;

        if (this.planModelService.readonly || skipSave) {
            runValidations = this.runValidations();
        } else {
            this.planLodgementService.increaseValidationAttempt();
            runValidations = this.planModelService.save().pipe(switchMap(() => this.runValidations()));
        }

        const subscription = runValidations.pipe(first()).subscribe();

        this.eventBus.cast('nlr:busy', subscription);
    }

    private mergeDependantPlans(dstArray: DependantPlan[], srcArray: DependantPlan[]) {
        const itemsToKeep = dstArray.filter(d => srcArray.some(s => this.dependantPlansMatch(d, s) && s.parentPlan === d.parentPlan));
        const itemsToAdd = srcArray.filter(s => !dstArray.some(d => this.dependantPlansMatch(d, s) && d.parentPlan === s.parentPlan));
        dstArray.splice(0, dstArray.length, ...itemsToKeep, ...itemsToAdd);
    }

    private mergeStrataPlanActions(dstArray: StrataPlanAction[], srcArray: StrataPlanAction[]) {
        const itemsToKeep = dstArray.filter(d => srcArray.some(s => this.strataPlanActionsMatch(d, s)));
        const itemsToAdd = srcArray.filter(s => !dstArray.some(d => this.strataPlanActionsMatch(d, s)));
        dstArray.splice(0, dstArray.length, ...itemsToKeep, ...itemsToAdd);
    }

    private dependantPlansMatch(dst: DependantPlan, src: DependantPlan) {
        return (
            src.planType.id === dst.planType.id &&
            src.planPurpose.id === dst.planPurpose.id &&
            src.planNumber === dst.planNumber &&
            src.dependencyType === dst.dependencyType
        );
    }

    private strataPlanActionsMatch(dst: StrataPlanAction, src: StrataPlanAction) {
        return (
            src.planType.id === dst.planType.id &&
            src.planPurpose.id === dst.planPurpose.id &&
            src.planNumber === dst.planNumber &&
            src.dependencyType === dst.dependencyType
        );
    }

    private runValidations() {
        return this.nlrPlanDetailService.runValidationsForStage(this.stage);
    }
}
